import { t } from 'i18next';
import {
  DoNotCallRestrictionType,
  ClinicDoNotCallRestrictionType,
} from 'infrastructure/enums';
import { doNotCallRestrictionTypeDataSource } from 'infrastructure/data-sources/do-not-call-restriction-types';

import { useRpmPatientDetails } from '../rpm';
import { useCallAccess } from './use-call-access';

import type { Nullable } from 'infrastructure/types';

const messageMap: Record<string, (source: string) => string> = {
  [DoNotCallRestrictionType.FullRestriction]: (source: string) =>
    t('messages.doNotCall', {
      source,
    }),
  [DoNotCallRestrictionType.LimitedHours]: (source: string) =>
    t('messages.doNotCallLimitedHours', {
      source,
    }),
};

const buildDoNotCallReasonMessage = ({
  canCallPatient,
  canCallClinic,
  patientRestriction,
  clinicRestriction,
  label,
}: {
  canCallPatient: boolean;
  canCallClinic: boolean;
  patientRestriction?: Nullable<DoNotCallRestrictionType>;
  clinicRestriction?: Nullable<ClinicDoNotCallRestrictionType>;
  label: 'RPM' | 'CCM';
}) => {
  if (canCallPatient && canCallClinic) return '';

  if (!canCallPatient) {
    const rt = patientRestriction ?? DoNotCallRestrictionType.FullRestriction;
    const reason = messageMap[rt];
    return reason(t('labels.patient'));
  }

  if (!canCallClinic) {
    const rt =
      clinicRestriction ?? ClinicDoNotCallRestrictionType.FullRestriction;
    const reason = messageMap[rt];
    return reason(t('labels.clinic'));
  }

  return label;
};

interface IProps {
  patientGuid: string;
}

export const usePatientDoNotCallInfo = ({ patientGuid }: IProps) => {
  const {
    query: { patient },
  } = useRpmPatientDetails({ patientGuid });

  const profile = patient?.profile;
  const clinic = patient?.clinic;

  const patientRestrictions = doNotCallRestrictionTypeDataSource({
    type: 'patient',
  });
  const clinicRestrictions = doNotCallRestrictionTypeDataSource({
    type: 'clinic',
  });

  const patientAccess = useCallAccess({
    doNotCallRestrictionType: profile?.doNotCallRestrictionType,
    ccmDoNotCallRestrictionType: profile?.ccmDoNotCallRestrictionType,
    doNotCallStartTime: profile?.doNotCallStartTime,
    doNotCallEndTime: profile?.doNotCallEndTime,
    ccmDoNotCallStartTime: profile?.ccmDoNotCallStartTime,
    ccmDoNotCallEndTime: profile?.ccmDoNotCallEndTime,
    patientTimezone: profile?.timezone,
  });

  const clinicAccess = useCallAccess({
    doNotCallRestrictionType: clinic?.doNotCallRestrictionType,
    ccmDoNotCallRestrictionType: clinic?.ccmDoNotCallRestrictionType,
    doNotCallStartTime: clinic?.doNotCallStartTime,
    doNotCallEndTime: clinic?.doNotCallEndTime,
    ccmDoNotCallStartTime: clinic?.ccmDoNotCallStartTime,
    ccmDoNotCallEndTime: clinic?.ccmDoNotCallEndTime,
    patientTimezone: profile?.timezone,
  });

  const hasPatientRpmRestriction = !!patientRestrictions.findByField(
    'id',
    profile?.doNotCallRestrictionType,
  );
  const hasPatientCcmRestriction = !!patientRestrictions.findByField(
    'id',
    profile?.ccmDoNotCallRestrictionType,
  );

  const hasClinicRpmRestriction = !!clinicRestrictions.findByField(
    'id',
    clinic?.doNotCallRestrictionType,
  );
  const hasClinicCcmRestriction = !!clinicRestrictions.findByField(
    'id',
    clinic?.ccmDoNotCallRestrictionType,
  );

  let doNotCallRpm = false;
  let doNotCallCcm = false;

  let rpmRestrictionType;
  let ccmRestrictionType;

  if (hasPatientRpmRestriction) {
    doNotCallRpm = !patientAccess.canCallRpm;
    rpmRestrictionType = profile?.doNotCallRestrictionType;
  } else if (hasClinicRpmRestriction) {
    doNotCallRpm = !clinicAccess.canCallRpm;
    rpmRestrictionType = clinic?.doNotCallRestrictionType;
  }

  if (hasPatientCcmRestriction) {
    doNotCallCcm = !patientAccess.canCallCcm;
    ccmRestrictionType = profile?.ccmDoNotCallRestrictionType;
  } else if (hasClinicCcmRestriction) {
    doNotCallCcm = !clinicAccess.canCallCcm;
    ccmRestrictionType = clinic?.ccmDoNotCallRestrictionType;
  }

  const rpmDoNotCallReasonMessage = buildDoNotCallReasonMessage({
    canCallPatient: patientAccess.canCallRpm,
    canCallClinic: clinicAccess.canCallRpm,
    patientRestriction: profile?.doNotCallRestrictionType,
    clinicRestriction: clinic?.doNotCallRestrictionType,
    label: 'RPM',
  });

  const ccmDoNotCallReasonMessage = buildDoNotCallReasonMessage({
    canCallPatient: patientAccess.canCallCcm,
    canCallClinic: clinicAccess.canCallCcm,
    patientRestriction: profile?.ccmDoNotCallRestrictionType,
    clinicRestriction: clinic?.ccmDoNotCallRestrictionType,
    label: 'CCM',
  });

  return {
    patientDoNotCallRpm: !patientAccess.canCallRpm,
    patientDoNotCallCcm: !patientAccess.canCallCcm,
    clinicDoNotCallRpm: !clinicAccess.canCallRpm,
    clinicDoNotCallCcm: !clinicAccess.canCallCcm,

    doNotCallRpm,
    doNotCallCcm,

    rpmRestrictionType,
    ccmRestrictionType,

    rpmDoNotCallReasonMessage,
    ccmDoNotCallReasonMessage,
  };
};
